import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';

const MIN_AXIS_PIXEL_SIZE = 3;
const MAX_UNIT_PIXEL_SIZE = 42;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 6;
const ZOOM_SENSITIVITY = 0.002;
const MAP_PADDING = 18;
const ROW_HEADER_WIDTH = 74;
const COLUMN_HEADER_HEIGHT = 44;

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}

function getAxisSize(entry) {
  const size = Number(entry?.size);
  return Number.isFinite(size) && size > 0 ? size : 1;
}

function hasAxisPattern(entry) {
  const patterns = Array.isArray(entry?.pattern) ? entry.pattern : [entry?.pattern];
  return patterns.some((pattern) => typeof pattern === 'string' && pattern.trim());
}

function makeOffsets(sizes) {
  const offsets = [0];
  sizes.forEach((size) => offsets.push(offsets[offsets.length - 1] + size));
  return offsets;
}

function getLayout(containerWidth, containerHeight, columns, rows, fillAvailable, zoom) {
  const availableWidth = Math.max(420, containerWidth || 1100);
  const availableHeight = Math.max(220, containerHeight || 0);
  const columnUnits = columns.map(getAxisSize);
  const rowUnits = rows.map(getAxisSize);
  const totalColumnUnits = columnUnits.reduce((total, size) => total + size, 0);
  const totalRowUnits = rowUnits.reduce((total, size) => total + size, 0);
  const smallestAxisUnit = Math.min(...columnUnits, ...rowUnits);
  const minimumScale = MIN_AXIS_PIXEL_SIZE / smallestAxisUnit;
  const widthScale = (
    availableWidth - ROW_HEADER_WIDTH - (MAP_PADDING * 2)
  ) / totalColumnUnits;
  let unitScale = Math.max(minimumScale, Math.min(MAX_UNIT_PIXEL_SIZE, widthScale));

  if (fillAvailable && containerWidth && containerHeight) {
    const heightScale = (
      availableHeight - COLUMN_HEADER_HEIGHT - (MAP_PADDING * 2)
    ) / totalRowUnits;
    unitScale = Math.max(minimumScale, Math.min(widthScale, heightScale));
  }
  unitScale *= zoom;

  const columnWidths = columnUnits.map((size) => size * unitScale);
  const rowHeights = rowUnits.map((size) => size * unitScale);
  const columnOffsets = makeOffsets(columnWidths);
  const rowOffsets = makeOffsets(rowHeights);
  const gridWidth = columnOffsets[columnOffsets.length - 1];
  const gridHeight = rowOffsets[rowOffsets.length - 1];
  const contentWidth = MAP_PADDING + ROW_HEADER_WIDTH + gridWidth + MAP_PADDING;
  const contentHeight = MAP_PADDING + COLUMN_HEADER_HEIGHT + gridHeight + MAP_PADDING;

  return {
    columnOffsets,
    columnHeaderHeight: COLUMN_HEADER_HEIGHT,
    columnWidths,
    gridHeight,
    gridWidth,
    gridX: MAP_PADDING + ROW_HEADER_WIDTH,
    gridY: MAP_PADDING + COLUMN_HEADER_HEIGHT,
    height: fillAvailable ? Math.max(availableHeight, contentHeight) : contentHeight,
    rowHeights,
    rowOffsets,
    rowHeaderWidth: ROW_HEADER_WIDTH,
    unitScale,
    width: fillAvailable ? Math.max(availableWidth, contentWidth) : contentWidth,
  };
}

function findAxisIndex(position, offsets) {
  if (position < 0 || position >= offsets[offsets.length - 1]) return -1;
  let low = 0;
  let high = offsets.length - 2;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (position < offsets[middle]) high = middle - 1;
    else if (position >= offsets[middle + 1]) low = middle + 1;
    else return middle;
  }
  return -1;
}

function getCellBounds(layout, rowIndex, columnIndex) {
  return {
    height: layout.rowHeights[rowIndex],
    width: layout.columnWidths[columnIndex],
    x: layout.gridX + layout.columnOffsets[columnIndex],
    y: layout.gridY + layout.rowOffsets[rowIndex],
  };
}

function shorten(value, maxLength) {
  if (!value) return '';
  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
}

function getHeaderFill(palette, configured, selected) {
  if (selected) return palette.selected;
  if (configured) return palette.configured;
  return palette.header;
}

function colorWithAlpha(color, alpha) {
  const normalized = String(color || '').replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return `rgba(245, 158, 11, ${alpha})`;

  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function getRangeBounds(range, definition, layout) {
  const firstRow = definition.rows.findIndex((row) => row.id === range.rowStartId);
  const lastRow = definition.rows.findIndex((row) => row.id === range.rowEndId);
  const firstColumn = definition.columns.findIndex(
    (column) => column.id === range.columnStartId,
  );
  const lastColumn = definition.columns.findIndex(
    (column) => column.id === range.columnEndId,
  );

  if ([firstRow, lastRow, firstColumn, lastColumn].some((index) => index < 0)) return null;

  const rowStart = Math.min(firstRow, lastRow);
  const rowEnd = Math.max(firstRow, lastRow);
  const columnStart = Math.min(firstColumn, lastColumn);
  const columnEnd = Math.max(firstColumn, lastColumn);

  return {
    columnEnd,
    columnStart,
    height: layout.rowOffsets[rowEnd + 1] - layout.rowOffsets[rowStart],
    rowEnd,
    rowStart,
    width: layout.columnOffsets[columnEnd + 1] - layout.columnOffsets[columnStart],
    x: layout.gridX + layout.columnOffsets[columnStart],
    y: layout.gridY + layout.rowOffsets[rowStart],
  };
}

function drawCanvas({
  annotationPreview,
  canvas,
  cellHighlights,
  containerHeight,
  containerWidth,
  definition,
  fillAvailable,
  routePreviewIndex,
  routePreviewSegments,
  routePreviewStops,
  selectedAnnotationId,
  selection,
  zoom,
}) {
  const targetCanvas = canvas;
  const context = targetCanvas.getContext('2d');
  if (!context) return null;

  const layout = getLayout(
    containerWidth,
    containerHeight,
    definition.columns,
    definition.rows,
    fillAvailable,
    zoom,
  );
  const ratio = window.devicePixelRatio || 1;
  const dark = true;
  const palette = {
    background: '#2f312b',
    cell: '#3b3d36',
    grid: '#60635a',
    header: '#474942',
    configured: '#684d31',
    home: '#009cff',
    homeGrid: '#a5e3ff',
    selected: '#b96f32',
    roadMarking: 'rgba(255, 255, 255, 0.9)',
    walkable: '#0b0d0e',
    walkableGrid: '#303437',
    text: '#f3efe4',
    muted: '#c8c4b8',
  };

  targetCanvas.width = Math.round(layout.width * ratio);
  targetCanvas.height = Math.round(layout.height * ratio);
  targetCanvas.style.width = `${layout.width}px`;
  targetCanvas.style.height = `${layout.height}px`;

  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, layout.width, layout.height);
  context.fillStyle = palette.background;
  context.fillRect(0, 0, layout.width, layout.height);

  const selectedColumnIndex = selection?.axis === 'columns'
    ? definition.columns.findIndex((entry) => entry.id === selection.id)
    : -1;
  const selectedRowIndex = selection?.axis === 'rows'
    ? definition.rows.findIndex((entry) => entry.id === selection.id)
    : -1;
  const walkableCellKeys = new Set((definition.walkableCells || []).map(
    (cell) => `${cell.rowId}:${cell.columnId}`,
  ));
  const routeHomeKey = definition.routeHome
    ? `${definition.routeHome.rowId}:${definition.routeHome.columnId}`
    : null;

  context.lineWidth = 1;
  definition.rows.forEach((row, rowIndex) => {
    definition.columns.forEach((column, columnIndex) => {
      const bounds = getCellBounds(layout, rowIndex, columnIndex);
      const cellKey = `${row.id}:${column.id}`;
      const walkable = walkableCellKeys.has(cellKey);
      const routeHome = cellKey === routeHomeKey;
      context.fillStyle = palette.cell;
      context.strokeStyle = palette.grid;
      if (walkable) {
        context.fillStyle = palette.walkable;
        context.strokeStyle = palette.walkableGrid;
      }
      if (routeHome) context.fillStyle = palette.home;
      context.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
      if (routeHome) context.strokeStyle = palette.homeGrid;
      context.strokeRect(
        bounds.x + 0.5,
        bounds.y + 0.5,
        bounds.width,
        bounds.height,
      );
    });

    const headerX = MAP_PADDING;
    const headerY = layout.gridY + layout.rowOffsets[rowIndex];
    const rowHeight = layout.rowHeights[rowIndex];
    const configured = hasAxisPattern(row);
    const selected = rowIndex === selectedRowIndex;
    context.fillStyle = getHeaderFill(palette, configured, selected);
    context.fillRect(headerX, headerY, layout.rowHeaderWidth, rowHeight);
    context.strokeStyle = palette.grid;
    context.strokeRect(headerX + 0.5, headerY + 0.5, layout.rowHeaderWidth, rowHeight);
    context.fillStyle = palette.text;
    context.font = `${Math.max(8, Math.min(12, rowHeight - 4))}px sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(
      shorten(row.label, 10),
      headerX + (layout.rowHeaderWidth / 2),
      headerY + (rowHeight / 2),
    );
  });

  const roadDashLength = clamp(
    layout.unitScale * 0.28,
    2,
    7,
  );
  context.save();
  context.strokeStyle = palette.roadMarking;
  context.lineWidth = clamp(layout.unitScale * 0.1, 1, 2.4);
  context.lineCap = 'butt';
  context.setLineDash([roadDashLength, roadDashLength]);

  definition.rows.forEach((row, rowIndex) => {
    definition.columns.forEach((column, columnIndex) => {
      const cellKey = `${row.id}:${column.id}`;
      if (!walkableCellKeys.has(cellKey) || cellKey === routeHomeKey) return;

      const bounds = getCellBounds(layout, rowIndex, columnIndex);
      const centerX = bounds.x + (bounds.width / 2);
      const centerY = bounds.y + (bounds.height / 2);
      const neighbors = [
        {
          connected: rowIndex > 0 && walkableCellKeys.has(
            `${definition.rows[rowIndex - 1].id}:${column.id}`,
          ),
          x: centerX,
          y: bounds.y,
        },
        {
          connected: columnIndex < definition.columns.length - 1 && walkableCellKeys.has(
            `${row.id}:${definition.columns[columnIndex + 1].id}`,
          ),
          x: bounds.x + bounds.width,
          y: centerY,
        },
        {
          connected: rowIndex < definition.rows.length - 1 && walkableCellKeys.has(
            `${definition.rows[rowIndex + 1].id}:${column.id}`,
          ),
          x: centerX,
          y: bounds.y + bounds.height,
        },
        {
          connected: columnIndex > 0 && walkableCellKeys.has(
            `${row.id}:${definition.columns[columnIndex - 1].id}`,
          ),
          x: bounds.x,
          y: centerY,
        },
      ].filter((neighbor) => neighbor.connected);

      if (neighbors.length >= 3) return;

      context.beginPath();
      if (neighbors.length === 0) {
        context.moveTo(centerX - (bounds.width * 0.22), centerY);
        context.lineTo(centerX + (bounds.width * 0.22), centerY);
      } else {
        neighbors.forEach((neighbor) => {
          context.moveTo(centerX, centerY);
          context.lineTo(neighbor.x, neighbor.y);
        });
      }
      context.stroke();
    });
  });
  context.restore();

  if (definition.routeHome) {
    const homeRowIndex = definition.rows.findIndex(
      (row) => row.id === definition.routeHome.rowId,
    );
    const homeColumnIndex = definition.columns.findIndex(
      (column) => column.id === definition.routeHome.columnId,
    );
    if (homeRowIndex >= 0 && homeColumnIndex >= 0) {
      const bounds = getCellBounds(layout, homeRowIndex, homeColumnIndex);
      const centerX = bounds.x + (bounds.width / 2);
      const centerY = bounds.y + (bounds.height / 2);
      const outerRadius = clamp(Math.min(bounds.width, bounds.height) * 0.34, 3, 10);
      const innerRadius = outerRadius * 0.45;
      context.beginPath();
      for (let point = 0; point < 10; point += 1) {
        const radius = point % 2 === 0 ? outerRadius : innerRadius;
        const angle = (-Math.PI / 2) + ((point * Math.PI) / 5);
        const x = centerX + (Math.cos(angle) * radius);
        const y = centerY + (Math.sin(angle) * radius);
        if (point === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      }
      context.closePath();
      context.fillStyle = '#ffffff';
      context.fill();
    }
  }

  const annotations = [...(definition.annotations || [])];
  if (annotationPreview) {
    annotations.push({
      ...annotationPreview,
      color: '#f59e0b',
      id: '__preview__',
      title: '',
    });
  }

  annotations.forEach((annotation) => {
    const bounds = getRangeBounds(annotation, definition, layout);
    if (!bounds) return;

    context.fillStyle = colorWithAlpha(annotation.color, dark ? 0.24 : 0.18);
    context.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
    context.strokeStyle = annotation.color;
    context.lineWidth = annotation.id === selectedAnnotationId ? 3 : 2;
    context.setLineDash(annotation.id === '__preview__' ? [6, 4] : []);
    context.strokeRect(
      bounds.x + 1,
      bounds.y + 1,
      Math.max(0, bounds.width - 2),
      Math.max(0, bounds.height - 2),
    );
    context.setLineDash([]);

    if (annotation.title && bounds.width >= 54 && bounds.height >= 18) {
      context.fillStyle = dark ? '#fff8ed' : '#172033';
      context.font = '600 11px sans-serif';
      context.textAlign = 'left';
      context.textBaseline = 'top';
      context.fillText(shorten(annotation.title, 24), bounds.x + 5, bounds.y + 4);
    }
  });

  context.fillStyle = dark ? 'rgba(245, 158, 11, 0.10)' : 'rgba(249, 115, 22, 0.08)';
  if (selectedColumnIndex >= 0) {
    context.fillRect(
      layout.gridX + layout.columnOffsets[selectedColumnIndex],
      layout.gridY,
      layout.columnWidths[selectedColumnIndex],
      layout.gridHeight,
    );
  }

  if (selectedRowIndex >= 0) {
    context.fillRect(
      layout.gridX,
      layout.gridY + layout.rowOffsets[selectedRowIndex],
      layout.gridWidth,
      layout.rowHeights[selectedRowIndex],
    );
  }

  cellHighlights.forEach((highlight) => {
    (highlight.targetCells || []).forEach((cell) => {
      const rowIndex = definition.rows.findIndex((row) => row.id === cell.rowId);
      const columnIndex = definition.columns.findIndex(
        (column) => column.id === cell.columnId,
      );
      if (rowIndex < 0 || columnIndex < 0) return;

      const bounds = getCellBounds(layout, rowIndex, columnIndex);
      context.save();
      context.globalAlpha = highlight.opacity ?? 0.86;
      context.fillStyle = highlight.color;
      context.fillRect(
        bounds.x + 1,
        bounds.y + 1,
        Math.max(0, bounds.width - 2),
        Math.max(0, bounds.height - 2),
      );
      context.globalAlpha = 1;
      context.strokeStyle = highlight.outline || 'rgba(255, 255, 255, 0.74)';
      context.lineWidth = 1.5;
      context.strokeRect(
        bounds.x + 1.5,
        bounds.y + 1.5,
        Math.max(0, bounds.width - 3),
        Math.max(0, bounds.height - 3),
      );
      context.restore();
    });
  });

  let visibleRouteSegmentCount = Math.min(
    routePreviewSegments.length,
    routePreviewIndex + 1,
  );
  if (
    routePreviewIndex >= 0
    && routePreviewIndex === routePreviewStops.length - 1
  ) {
    visibleRouteSegmentCount = routePreviewSegments.length;
  }

  const rowIndexes = new Map(definition.rows.map((row, index) => [row.id, index]));
  const columnIndexes = new Map(
    definition.columns.map((column, index) => [column.id, index]),
  );
  const routeTraversals = [];
  routePreviewSegments.slice(0, visibleRouteSegmentCount).forEach((segment) => {
    const points = (segment.cells || []).map((cell) => {
      const rowIndex = rowIndexes.get(cell.rowId);
      const columnIndex = columnIndexes.get(cell.columnId);
      if (!Number.isInteger(rowIndex) || !Number.isInteger(columnIndex)) return null;
      const bounds = getCellBounds(layout, rowIndex, columnIndex);
      return {
        columnIndex,
        key: `${cell.rowId}:${cell.columnId}`,
        rowIndex,
        x: bounds.x + (bounds.width / 2),
        y: bounds.y + (bounds.height / 2),
      };
    }).filter(Boolean);
    if (points.length < 2) return;

    points.slice(1).forEach((point, pointIndex) => {
      const previousPoint = points[pointIndex];
      const edgeKey = [previousPoint.key, point.key].sort().join('|');
      routeTraversals.push({ edgeKey, from: previousPoint, to: point });
    });
  });

  const edgeDirections = new Map();
  routeTraversals.forEach((traversal) => {
    if (!edgeDirections.has(traversal.edgeKey)) {
      edgeDirections.set(traversal.edgeKey, new Set());
    }
    edgeDirections.get(traversal.edgeKey).add(`${traversal.from.key}>${traversal.to.key}`);
  });

  const homeRowIndex = rowIndexes.get(definition.routeHome?.rowId);
  const homeColumnIndex = columnIndexes.get(definition.routeHome?.columnId);
  const hasRouteHome = Number.isInteger(homeRowIndex) && Number.isInteger(homeColumnIndex);
  const homeBounds = hasRouteHome
    ? getCellBounds(layout, homeRowIndex, homeColumnIndex)
    : null;
  const homeX = homeBounds ? homeBounds.x + (homeBounds.width / 2) : null;
  const homeY = homeBounds ? homeBounds.y + (homeBounds.height / 2) : null;
  const routeLineWidth = clamp(
    layout.unitScale * 0.18,
    1.75,
    4.5,
  );
  const directedTraversalCounts = new Map();

  context.save();
  context.lineCap = 'round';
  context.lineJoin = 'round';
  routeTraversals.forEach((traversal) => {
    const fromHomeDistance = hasRouteHome
      ? Math.abs(traversal.from.x - homeX) + Math.abs(traversal.from.y - homeY)
      : Number.POSITIVE_INFINITY;
    const toHomeDistance = hasRouteHome
      ? Math.abs(traversal.to.x - homeX) + Math.abs(traversal.to.y - homeY)
      : Number.POSITIVE_INFINITY;
    const towardHome = toHomeDistance < fromHomeDistance;
    const directionKey = `${traversal.from.key}>${traversal.to.key}`;
    const directedKey = `${traversal.edgeKey}:${directionKey}`;
    const traversalCount = (directedTraversalCounts.get(directedKey) || 0) + 1;
    directedTraversalCounts.set(directedKey, traversalCount);

    const repeatLevel = Math.min(4, traversalCount - 1);
    const baseHue = towardHome ? 145 : 330;
    const hue = baseHue - (repeatLevel * 7);
    const baseLightness = dark ? 62 : 52;
    const lightness = Math.min(90, baseLightness + (repeatLevel * 9));
    const baseAlpha = dark ? 0.64 : 0.54;
    const alpha = Math.min(0.96, baseAlpha + (repeatLevel * 0.1));
    const splitDirections = edgeDirections.get(traversal.edgeKey).size > 1;
    const deltaX = traversal.to.x - traversal.from.x;
    const deltaY = traversal.to.y - traversal.from.y;
    const edgeLength = Math.sqrt((deltaX ** 2) + (deltaY ** 2)) || 1;
    const laneOffset = splitDirections ? routeLineWidth * 0.52 : 0;
    const offsetX = (deltaY / edgeLength) * laneOffset;
    const offsetY = (-deltaX / edgeLength) * laneOffset;

    context.lineWidth = splitDirections ? routeLineWidth * 0.62 : routeLineWidth;
    context.strokeStyle = `hsla(${hue}, 100%, ${lightness}%, ${alpha})`;
    context.beginPath();
    context.moveTo(traversal.from.x + offsetX, traversal.from.y + offsetY);
    context.lineTo(traversal.to.x + offsetX, traversal.to.y + offsetY);
    context.stroke();
  });
  context.restore();

  routePreviewStops.slice(0, routePreviewIndex + 1).forEach((stop, stopIndex) => {
    const active = stopIndex === routePreviewIndex;
    const progress = routePreviewStops.length <= 1
      ? 0
      : stopIndex / (routePreviewStops.length - 1);
    const hue = Math.round(205 - (progress * 85));

    (stop.targetCells || []).forEach((cell) => {
      const rowIndex = definition.rows.findIndex((row) => row.id === cell.rowId);
      const columnIndex = definition.columns.findIndex(
        (column) => column.id === cell.columnId,
      );
      if (rowIndex < 0 || columnIndex < 0) return;

      const bounds = getCellBounds(layout, rowIndex, columnIndex);
      context.save();
      context.globalAlpha = active ? 0.98 : 0.76;
      context.fillStyle = active ? '#22d3ee' : `hsl(${hue}, 82%, 48%)`;
      context.fillRect(
        bounds.x + 1,
        bounds.y + 1,
        Math.max(0, bounds.width - 2),
        Math.max(0, bounds.height - 2),
      );
      context.globalAlpha = 1;
      context.strokeStyle = active ? '#ffffff' : `hsl(${hue}, 92%, 78%)`;
      context.lineWidth = active ? 3 : 1.5;
      context.strokeRect(
        bounds.x + 1.5,
        bounds.y + 1.5,
        Math.max(0, bounds.width - 3),
        Math.max(0, bounds.height - 3),
      );
      if (bounds.width >= 14 && bounds.height >= 14) {
        context.fillStyle = '#07111d';
        context.font = `700 ${clamp(Math.min(bounds.width, bounds.height) * 0.5, 8, 13)}px sans-serif`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(
          String(stop.sequence),
          bounds.x + (bounds.width / 2),
          bounds.y + (bounds.height / 2),
        );
      }
      context.restore();
    });
  });

  definition.columns.forEach((column, columnIndex) => {
    const headerX = layout.gridX + layout.columnOffsets[columnIndex];
    const headerY = MAP_PADDING;
    const columnWidth = layout.columnWidths[columnIndex];
    const configured = hasAxisPattern(column);
    const selected = columnIndex === selectedColumnIndex;
    context.fillStyle = getHeaderFill(palette, configured, selected);
    context.fillRect(headerX, headerY, columnWidth, layout.columnHeaderHeight);
    context.strokeStyle = palette.grid;
    context.strokeRect(headerX + 0.5, headerY + 0.5, columnWidth, layout.columnHeaderHeight);
    context.save();
    context.translate(headerX + (columnWidth / 2), headerY + (layout.columnHeaderHeight / 2));
    if (columnWidth < 28) context.rotate(-Math.PI / 2);
    context.fillStyle = palette.text;
    context.font = '11px sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(
      shorten(column.label, columnWidth < 28 ? 7 : 5),
      0,
      0,
    );
    context.restore();
  });

  context.fillStyle = palette.muted;
  context.font = '11px sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(
    `${definition.columns.length} × ${definition.rows.length}`,
    MAP_PADDING + (layout.rowHeaderWidth / 2),
    MAP_PADDING + (layout.columnHeaderHeight / 2),
  );

  return layout;
}

function WarehouseMapDesignerCanvas({
  cellHighlights,
  definition,
  fillAvailable,
  interactive,
  onCreateAnnotation,
  onPaintCell,
  onSelectAnnotation,
  onSelectAxis,
  onSetRouteHome,
  routePreviewIndex,
  routePreviewSegments,
  routePreviewStops,
  selectedAnnotationId,
  selection,
  tool,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const layoutRef = useRef(null);
  const dragRef = useRef(null);
  const pendingZoomAnchorRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ height: 0, width: 0 });
  const [annotationPreview, setAnnotationPreview] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateSize = () => setContainerSize({
      height: container.clientHeight,
      width: container.clientWidth,
    });
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleWheel = (event) => {
      if (!event.ctrlKey) return;
      event.preventDefault();

      const layout = layoutRef.current;
      if (!layout) return;

      const bounds = container.getBoundingClientRect();
      const pointerX = clamp(event.clientX - bounds.left, 0, container.clientWidth);
      const pointerY = clamp(event.clientY - bounds.top, 0, container.clientHeight);
      const logicalX = (
        container.scrollLeft + pointerX - layout.gridX
      ) / layout.unitScale;
      const logicalY = (
        container.scrollTop + pointerY - layout.gridY
      ) / layout.unitScale;
      let { deltaY } = event;
      const { deltaMode } = event;
      if (deltaMode === WheelEvent.DOM_DELTA_LINE) deltaY *= 16;
      if (deltaMode === WheelEvent.DOM_DELTA_PAGE) deltaY *= container.clientHeight;
      const zoomFactor = Math.exp(-clamp(deltaY, -240, 240) * ZOOM_SENSITIVITY);

      setZoom((currentZoom) => {
        const nextZoom = clamp(currentZoom * zoomFactor, MIN_ZOOM, MAX_ZOOM);
        if (Math.abs(nextZoom - currentZoom) < 0.0001) return currentZoom;
        pendingZoomAnchorRef.current = {
          logicalX,
          logicalY,
          pointerX,
          pointerY,
        };
        return nextZoom;
      });
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    const nextLayout = drawCanvas({
      annotationPreview,
      canvas: canvasRef.current,
      cellHighlights,
      containerHeight: containerSize.height,
      containerWidth: containerSize.width,
      definition,
      fillAvailable,
      routePreviewIndex,
      routePreviewSegments,
      routePreviewStops,
      selectedAnnotationId,
      selection,
      zoom,
    });
    layoutRef.current = nextLayout;

    const zoomAnchor = pendingZoomAnchorRef.current;
    const container = containerRef.current;
    if (zoomAnchor && container && nextLayout) {
      container.scrollLeft = nextLayout.gridX
        + (zoomAnchor.logicalX * nextLayout.unitScale)
        - zoomAnchor.pointerX;
      container.scrollTop = nextLayout.gridY
        + (zoomAnchor.logicalY * nextLayout.unitScale)
        - zoomAnchor.pointerY;
      pendingZoomAnchorRef.current = null;
    }
  }, [
    annotationPreview,
    cellHighlights,
    containerSize,
    definition,
    fillAvailable,
    routePreviewIndex,
    routePreviewSegments,
    routePreviewStops,
    selectedAnnotationId,
    selection,
    zoom,
  ]);

  const getPointerTarget = (event) => {
    const canvas = canvasRef.current;
    const layout = layoutRef.current;
    if (!canvas || !layout) return null;

    const bounds = canvas.getBoundingClientRect();
    const x = (event.clientX - bounds.left) * (layout.width / bounds.width);
    const y = (event.clientY - bounds.top) * (layout.height / bounds.height);
    const columnIndex = findAxisIndex(x - layout.gridX, layout.columnOffsets);
    const rowIndex = findAxisIndex(y - layout.gridY, layout.rowOffsets);
    const insideGrid = columnIndex >= 0 && rowIndex >= 0;

    return {
      columnIndex,
      insideGrid,
      layout,
      rowIndex,
      x,
      y,
    };
  };

  const getCell = (target) => {
    if (!target?.insideGrid) return null;
    return {
      columnId: definition.columns[target.columnIndex].id,
      rowId: definition.rows[target.rowIndex].id,
    };
  };

  const paintTarget = (target) => {
    const cell = getCell(target);
    if (!cell) return;
    const key = `${cell.rowId}:${cell.columnId}`;
    if (dragRef.current?.lastKey === key) return;
    if (dragRef.current) dragRef.current.lastKey = key;
    onPaintCell(cell.rowId, cell.columnId, tool === 'paint');
  };

  const handleMouseDown = (event) => {
    if (!interactive) return;
    const target = getPointerTarget(event);
    const cell = getCell(target);
    if (!cell) return;

    if (tool === 'paint' || tool === 'erase') {
      dragRef.current = { type: tool, lastKey: null };
      paintTarget(target);
      event.preventDefault();
    } else if (tool === 'annotate') {
      const preview = {
        rowStartId: cell.rowId,
        rowEndId: cell.rowId,
        columnStartId: cell.columnId,
        columnEndId: cell.columnId,
      };
      dragRef.current = { type: tool, start: cell };
      setAnnotationPreview(preview);
      event.preventDefault();
    }
  };

  const handleMouseMove = (event) => {
    if (!interactive) return;
    if (!dragRef.current) return;
    const target = getPointerTarget(event);
    const cell = getCell(target);
    if (!cell) return;

    if (dragRef.current.type === 'paint' || dragRef.current.type === 'erase') {
      paintTarget(target);
    } else if (dragRef.current.type === 'annotate') {
      setAnnotationPreview({
        rowStartId: dragRef.current.start.rowId,
        rowEndId: cell.rowId,
        columnStartId: dragRef.current.start.columnId,
        columnEndId: cell.columnId,
      });
    }
  };

  const finishDrag = () => {
    if (!interactive) return;
    if (dragRef.current?.type === 'annotate' && annotationPreview) {
      onCreateAnnotation(annotationPreview);
    }
    dragRef.current = null;
    setAnnotationPreview(null);
  };

  const handleClick = (event) => {
    if (!interactive) return;
    const target = getPointerTarget(event);
    if (!target) return;
    const {
      columnIndex, layout, rowIndex, x, y,
    } = target;

    if (
      y >= MAP_PADDING
      && y < layout.gridY
      && columnIndex >= 0
      && columnIndex < definition.columns.length
    ) {
      onSelectAxis('columns', definition.columns[columnIndex].id);
      return;
    }

    if (
      x >= MAP_PADDING
      && x < layout.gridX
      && rowIndex >= 0
      && rowIndex < definition.rows.length
    ) {
      onSelectAxis('rows', definition.rows[rowIndex].id);
      return;
    }

    if (tool === 'select' && target.insideGrid) {
      const annotation = [...(definition.annotations || [])].reverse().find((candidate) => {
        const annotationBounds = getRangeBounds(candidate, definition, layout);
        return annotationBounds
          && columnIndex >= annotationBounds.columnStart
          && columnIndex <= annotationBounds.columnEnd
          && rowIndex >= annotationBounds.rowStart
          && rowIndex <= annotationBounds.rowEnd;
      });
      if (annotation) onSelectAnnotation(annotation.id);
    } else if (tool === 'home' && target.insideGrid) {
      const cell = getCell(target);
      if (cell) onSetRouteHome(cell.rowId, cell.columnId);
    }
  };

  let canvasCursor = 'default';
  if (interactive) canvasCursor = tool === 'select' ? 'pointer' : 'crosshair';

  return (
    <div
      className={`warehouse-map-designer__canvas-scroll${fillAvailable ? ' warehouse-map-designer__canvas-scroll--fill' : ''}`}
      ref={containerRef}
      style={{ overflow: fillAvailable && zoom <= 1 ? 'hidden' : 'auto' }}
      title="Hold Ctrl and use the mouse wheel to zoom toward the pointer."
    >
      <canvas
        aria-label={interactive
          ? 'Warehouse logical grid and walkable-area editor.'
          : 'Warehouse map.'}
        className="warehouse-map-designer__canvas"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseLeave={finishDrag}
        onMouseMove={handleMouseMove}
        onMouseUp={finishDrag}
        ref={canvasRef}
        style={{ cursor: canvasCursor }}
      />
    </div>
  );
}

WarehouseMapDesignerCanvas.propTypes = {
  cellHighlights: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string.isRequired,
    opacity: PropTypes.number,
    targetCells: PropTypes.arrayOf(PropTypes.shape({
      columnId: PropTypes.string.isRequired,
      rowId: PropTypes.string.isRequired,
    })).isRequired,
  })),
  definition: PropTypes.shape({
    annotations: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
    })),
    columns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      pattern: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      size: PropTypes.number,
    })).isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      pattern: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      size: PropTypes.number,
    })).isRequired,
    routeHome: PropTypes.shape({
      columnId: PropTypes.string.isRequired,
      rowId: PropTypes.string.isRequired,
    }),
    walkableCells: PropTypes.arrayOf(PropTypes.shape({
      columnId: PropTypes.string.isRequired,
      rowId: PropTypes.string.isRequired,
    })),
  }).isRequired,
  fillAvailable: PropTypes.bool,
  interactive: PropTypes.bool,
  onCreateAnnotation: PropTypes.func.isRequired,
  onPaintCell: PropTypes.func.isRequired,
  onSelectAnnotation: PropTypes.func.isRequired,
  onSelectAxis: PropTypes.func.isRequired,
  onSetRouteHome: PropTypes.func.isRequired,
  routePreviewIndex: PropTypes.number,
  routePreviewSegments: PropTypes.arrayOf(PropTypes.shape({
    cells: PropTypes.arrayOf(PropTypes.shape({
      columnId: PropTypes.string.isRequired,
      rowId: PropTypes.string.isRequired,
    })).isRequired,
  })),
  routePreviewStops: PropTypes.arrayOf(PropTypes.shape({
    sequence: PropTypes.number.isRequired,
    targetCells: PropTypes.arrayOf(PropTypes.shape({
      columnId: PropTypes.string.isRequired,
      rowId: PropTypes.string.isRequired,
    })),
  })),
  selectedAnnotationId: PropTypes.string,
  selection: PropTypes.shape({
    axis: PropTypes.oneOf(['columns', 'rows']),
    id: PropTypes.string,
  }),
  tool: PropTypes.oneOf(['select', 'paint', 'erase', 'annotate', 'home']).isRequired,
};

WarehouseMapDesignerCanvas.defaultProps = {
  cellHighlights: [],
  fillAvailable: false,
  interactive: true,
  routePreviewIndex: -1,
  routePreviewSegments: [],
  routePreviewStops: [],
  selectedAnnotationId: null,
  selection: null,
};

export default WarehouseMapDesignerCanvas;
